/**
 * searchUrlState — encode/decode listing view state (sort, view tab, query,
 * filters) into URL query parameters.
 *
 * Why a hand-rolled codec instead of JSON: the URL has to stay readable and
 * shareable ("look at this filtered view" is an external Trigger), and it has to
 * be *version gated* — a stale link must be ignored rather than applied, because
 * a filter that can no longer match anything reads to the user as "my inventory
 * vanished" and refreshing cannot fix it.
 *
 * Parameter names are deliberate:
 *   sort, dir   — sort field + direction
 *   view        — the active view tab (active / pending_info / archived / draft)
 *   query       — the committed search term. NOT `q`: `q` is already claimed by
 *                 the buyer-digest saved-search deep link on /menu/:c/:m, which
 *                 hydrates once and strips itself.
 *   f           — compact filter payload, `1|key:value~key:value`
 */

import type {
  ListingFilters,
  ListingSortField,
  ListingSortDirection,
  ComplianceCheck,
  AuditCheck,
} from '@/types/search';

export const FILTER_PARAM = 'f';
export const QUERY_PARAM = 'query';
export const SORT_PARAM = 'sort';
export const DIR_PARAM = 'dir';
export const VIEW_PARAM = 'view';

/** Bump when a filter key's meaning changes; older payloads are then ignored. */
export const FILTER_CODEC_VERSION = 1;

const PAIR_SEPARATOR = '~';
const KEY_SEPARATOR = ':';
const LIST_SEPARATOR = ',';

const SORT_FIELDS: ListingSortField[] = ['relevance', 'date', 'price'];
const SORT_DIRECTIONS: ListingSortDirection[] = ['asc', 'desc'];

export const VIEW_FILTERS = ['active', 'pending_info', 'archived', 'draft'] as const;
export type ViewFilterValue = (typeof VIEW_FILTERS)[number];

const COMPLIANCE_VALUES: ComplianceCheck[] = [
  'no_pesticides',
  'no_heavy_metals',
  'no_mycotoxins',
  'microbials_pass',
  'no_solvents',
  'moisture_pass',
  'foreign_matter_pass',
];

const AUDIT_VALUES: AuditCheck[] = [
  'missing_coa',
  'missing_images',
  'missing_price',
  'missing_quantity',
  'missing_thc',
  'missing_region',
  'missing_species',
  'missing_product_form',
];

type FilterKind = 'text' | 'textList' | 'number' | 'boolean' | 'compliance' | 'audit';

/**
 * The allowlist. A key absent here is never written to the URL and never read
 * from it — that is how `supplierToken` (anonymous supplier handle) stays out of
 * shareable links: a broker's supplier facet must not travel in a URL.
 */
const FILTER_SCHEMA: Partial<Record<keyof ListingFilters, FilterKind>> = {
  textSearch: 'text',
  productForm: 'textList',
  species: 'textList',
  region: 'textList',
  sellerCompany: 'text',
  certifications: 'text',
  thcMin: 'number',
  thcMax: 'number',
  cbdMin: 'number',
  cbdMax: 'number',
  terpeneMin: 'number',
  terpeneMax: 'number',
  priceMin: 'number',
  priceMax: 'number',
  hasCOA: 'boolean',
  hasImages: 'boolean',
  compliance: 'compliance',
  audit: 'audit',
};

export type EncodableFilterKey = keyof typeof FILTER_SCHEMA;

function encodeToken(value: string): string {
  // Escape our own separators plus anything the URL would mangle.
  return encodeURIComponent(value)
    .replace(/~/g, '%7E')
    .replace(/,/g, '%2C')
    .replace(/:/g, '%3A');
}

function decodeToken(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

export interface EncodeFiltersOptions {
  /** Keys to leave out of the URL entirely (e.g. supplier facets on buyer routes). */
  omitKeys?: EncodableFilterKey[];
}

/** Encode filters into the compact `f=` payload. Returns '' when nothing is set. */
export function encodeFilters(filters: ListingFilters, options: EncodeFiltersOptions = {}): string {
  const omit = new Set<string>(options.omitKeys ?? []);
  const parts: string[] = [];

  for (const [key, kind] of Object.entries(FILTER_SCHEMA) as [EncodableFilterKey, FilterKind][]) {
    if (omit.has(key)) continue;
    const value = filters[key];
    if (value === undefined || value === null || value === '') continue;

    switch (kind) {
      case 'text':
        parts.push(`${key}${KEY_SEPARATOR}${encodeToken(String(value))}`);
        break;
      case 'textList':
      case 'compliance':
      case 'audit': {
        const list = Array.isArray(value) ? value : [value];
        const clean = list.map(v => String(v)).filter(v => v !== '');
        if (clean.length === 0) break;
        parts.push(`${key}${KEY_SEPARATOR}${clean.map(encodeToken).join(LIST_SEPARATOR)}`);
        break;
      }
      case 'number': {
        const num = Number(value);
        if (!Number.isFinite(num)) break;
        parts.push(`${key}${KEY_SEPARATOR}${num}`);
        break;
      }
      case 'boolean':
        if (value === true) parts.push(`${key}${KEY_SEPARATOR}1`);
        break;
    }
  }

  if (parts.length === 0) return '';
  return `${FILTER_CODEC_VERSION}|${parts.join(PAIR_SEPARATOR)}`;
}

/**
 * Decode an `f=` payload. Unknown keys, unknown enum members, malformed numbers
 * and any payload from a different codec version are dropped — an unusable
 * filter is never applied.
 */
export function decodeFilters(payload: string | null | undefined): ListingFilters {
  if (!payload) return {};
  const pipe = payload.indexOf('|');
  if (pipe < 1) return {};
  const version = Number(payload.slice(0, pipe));
  if (version !== FILTER_CODEC_VERSION) return {};

  const out: ListingFilters = {};
  for (const pair of payload.slice(pipe + 1).split(PAIR_SEPARATOR)) {
    const sep = pair.indexOf(KEY_SEPARATOR);
    if (sep < 1) continue;
    const key = pair.slice(0, sep) as EncodableFilterKey;
    const raw = pair.slice(sep + 1);
    const kind = FILTER_SCHEMA[key];
    if (!kind || raw === '') continue;

    switch (kind) {
      case 'text':
        (out as Record<string, unknown>)[key] = decodeToken(raw);
        break;
      case 'textList': {
        const list = raw.split(LIST_SEPARATOR).map(decodeToken).filter(Boolean);
        if (list.length === 0) break;
        (out as Record<string, unknown>)[key] = list.length === 1 ? list[0] : list;
        break;
      }
      case 'compliance': {
        const list = raw
          .split(LIST_SEPARATOR)
          .map(decodeToken)
          .filter((v): v is ComplianceCheck => (COMPLIANCE_VALUES as string[]).includes(v));
        if (list.length > 0) out.compliance = list;
        break;
      }
      case 'audit': {
        const list = raw
          .split(LIST_SEPARATOR)
          .map(decodeToken)
          .filter((v): v is AuditCheck => (AUDIT_VALUES as string[]).includes(v));
        if (list.length > 0) out.audit = list;
        break;
      }
      case 'number': {
        const num = Number(raw);
        if (Number.isFinite(num)) (out as Record<string, unknown>)[key] = num;
        break;
      }
      case 'boolean':
        if (raw === '1') (out as Record<string, unknown>)[key] = true;
        break;
    }
  }
  return out;
}

export function parseSortField(value: string | null | undefined): ListingSortField | null {
  return value && (SORT_FIELDS as string[]).includes(value) ? (value as ListingSortField) : null;
}

export function parseSortDirection(value: string | null | undefined): ListingSortDirection | null {
  return value && (SORT_DIRECTIONS as string[]).includes(value)
    ? (value as ListingSortDirection)
    : null;
}

export function parseViewFilter(value: string | null | undefined): ViewFilterValue | null {
  return value && (VIEW_FILTERS as readonly string[]).includes(value)
    ? (value as ViewFilterValue)
    : null;
}
